import React, { useCallback } from "react";
import { connect } from "react-redux";
import { goToLessonQuestionIndex } from "../../processes/lessonQuestionProcess";
import { getLessonQuestionAnswered } from "../../selectors/answerSelector";
import { getCurrentLesson } from "../../selectors/lessonSelectors";
import { getFlattenedLessonQuestions } from "../../selectors/lessonQuestionSelectors";
import LessonQuestionPaginationItem from "./LessonQuestionPaginationItem";

const getFlowAnswersForQuestion = (state, { lessonId, questionId }) =>
  Object.values(state.entities.answers || {}).filter((a) => {
    if (!a || a.questionPromptId == null) return false;
    if (a.questionId == null || Number(a.questionId) !== Number(questionId)) return false;
    return a.lessonId != null && Number(a.lessonId) === Number(lessonId);
  });

const getPromptById = (state, promptId) => {
  if (promptId == null) return null;
  const questions = Object.values(state.entities.questions || {});
  for (const q of questions) {
    const prompts = q?.meta?.questionPrompts || [];
    const match = prompts.find((p) => Number(p.id) === Number(promptId));
    if (match) return match;
  }
  return null;
};

const isFlowComplete = (state, { lessonId, questionId }) => {
  const answers = getFlowAnswersForQuestion(state, { lessonId, questionId });
  return answers.some((a) => getPromptById(state, a.questionPromptId)?.isFinal === true);
};

const isFlowAllCorrect = (state, { lessonId, questionId }) => {
  const answers = getFlowAnswersForQuestion(state, { lessonId, questionId });
  if (answers.length === 0) return false;
  return answers.every((a) => a.correct === true);
};

function LessonQuestionPaginationItemContainer(props) {
  const { dispatch, index, isLocked } = props;

  const goToIndexHandler = useCallback(
    () => {
      if (isLocked) return;
      goToLessonQuestionIndex(dispatch, index);
    },
    [dispatch, index, isLocked]
  );

  return (
    <LessonQuestionPaginationItem {...props} goToIndexHandler={goToIndexHandler} />
  );
}

function mapStateToProps(state, ownProps) {
  const { question, stem, displayIndex, isFlow, index } = ownProps;
  const lesson = getCurrentLesson(state, state.currentLessonId);
  const lessonId = lesson?.id;
  const questionId = question?.id;
  const questionIsInteractive = question?.format === "interactive";

  let answered = null;
  let isLocked = false;

  if (isFlow) {
    if (isFlowComplete(state, { lessonId, questionId })) {
      answered = { value: isFlowAllCorrect(state, { lessonId, questionId }) };
    }
  } else if (stem) {
    if (questionIsInteractive) {
      isLocked = !isFlowComplete(state, { lessonId, questionId });
    }
    answered = getLessonQuestionAnswered(state, { question, stem, lessonId });
  } else {
    answered = getLessonQuestionAnswered(state, { question, stem, lessonId });
  }

  // Additional lock: an entry that comes after an unfinished flow of the same question.
  if (!isLocked && index != null) {
    const flattened = getFlattenedLessonQuestions(state);
    const earlierFlowEntry = flattened.find(
      (e, i) => i < index && e.isFlow && Number(e.question?.id) === Number(questionId)
    );
    if (earlierFlowEntry && !isFlowComplete(state, { lessonId, questionId })) {
      isLocked = true;
    }
  }

  return {
    currentIndex: state.lesson.currentQuestionIndex,
    answered,
    displayIndex,
    isLocked,
  };
}

export default connect(mapStateToProps)(LessonQuestionPaginationItemContainer);
